import { isValidEmailAddress, stripHtml, MERGE_FIELD_REGEX } from '@/utils/email.util';
import moment from 'moment-timezone';
import {
    BROADCAST_STATUS,
    DEFAULT_BUILDER_JSON_CONTENT_STRING,
    DEFAULT_FROM_VALUE,
    DEFAULT_TOADDRESS_VALUE,
    DEFAULT_TEMPLATE_HTML,
    REQUEST_PAYLOAD_DATE_FORMAT,
    UNLAYER_BROADCAST_TEMPLATE_COMMAND,
    EMAIL_BROADCAST_PREVIEW,
    EMAIL_BROADCAST_SUBJECT,
    DEFAULT_RECIPIENT_VALUES as LEGACY_RECIPIENT_VALUES,
    DEFAULT_RECIPIENT_VALUES_LIQUID_SYNTAX as LIQUID_RECIPIENT_VALUES,
} from '../constants/emailBroadcast.constants';
import { STEPS } from '@/components/unlayer/constants/editor.constants';
import { SOURCES } from '@/components/unlayer/constants/unlayer.constants';

const REGEX_REQUEST_PAYLOAD_DATE_FORMAT = new RegExp(/^\d{2}-\d{2}-\d{4} \d{2}:\d{2}$/);

export const differenceById = (primary, secondary) => {
    if (!primary || !secondary) {
        return [];
    }

    const secondaryIds = secondary.map(({ id }) => id);

    return primary.filter(({ id }) => !secondaryIds.includes(id));
};

export const getRequestPayloadScheduleDateAsMomentObject = ({ scheduleDate, useContactTimeZone, timeZoneId }) => {
    return useContactTimeZone
        ? moment(scheduleDate, REQUEST_PAYLOAD_DATE_FORMAT)
        : moment.tz(scheduleDate, REQUEST_PAYLOAD_DATE_FORMAT, timeZoneId);
};

export const isScheduleDatePast = ({ scheduleDate, timeZoneId, useContactTimeZone }) => {
    const now = moment();
    const when = getRequestPayloadScheduleDateAsMomentObject({ scheduleDate, useContactTimeZone, timeZoneId });

    return when.isBefore(now, 'minute');
};

export const prepareDateTimePayload = (scheduleDate) => {
    const localDate = moment(scheduleDate, REQUEST_PAYLOAD_DATE_FORMAT).local();

    return moment(scheduleDate, REQUEST_PAYLOAD_DATE_FORMAT)
    .utc()
    .year(localDate.year())
    .month(localDate.month())
    .date(localDate.date())
    .hour(localDate.hour())
    .minute(localDate.minute())
    .toISOString();
};

export const userHasFullNameAndEmail = (user) => !!user.fullName?.trim().length && isValidEmailAddress(user.email);

export const sortPeopleByFullNameAndEmail = (p1, p2) => p1.fullName.trim().localeCompare(p2.fullName.trim()) || p1.email.localeCompare(p2.email);
const userIsActive = (user) => user.status === 'Active';

export const validSortedUsers = (users) => [...users].filter(userHasFullNameAndEmail).sort(sortPeopleByFullNameAndEmail);

export const activeSortedUsers = (users) => validSortedUsers(users)
    .filter(userIsActive)
    .filter(userHasFullNameAndEmail)
    .sort(sortPeopleByFullNameAndEmail);

export const validateBroadcast = (email, section = '', options = {}) => {
    const stepData = email[section];
    const validators = {
        [STEPS.AUDIENCE]() {
            return !!stepData.recipients?.length;
        },

        [STEPS.CONTENT]() {
            return stripHtml(stepData.htmlBody ?? '').length > 0;
        },

        [STEPS.SCHEDULE]() {
            // if scheduleDate is null, it will send immediately
            return stepData.scheduleDate === null || REGEX_REQUEST_PAYLOAD_DATE_FORMAT.test(stepData.scheduleDate);
        },

        [STEPS.SENDER]() {
            if (stepData.from) {
                if (stepData.from === DEFAULT_FROM_VALUE) return true;

                const emailAddress = getFromAddress(stepData.from);
                const isMergeField = MERGE_FIELD_REGEX.test(emailAddress);

                if (isValidEmailAddress(emailAddress) || isMergeField) return true;
            }

            return false;
        },

        [STEPS.SUBJECT]() {
            const subjectLength = (stepData.subject ?? '').length;
            const previewLength = (stepData.previewText ?? '').length;

            if (!options.enforceSubjectMaxLimit) {
                return subjectLength > 0;
            }

            return subjectLength > 0
                && subjectLength <= EMAIL_BROADCAST_SUBJECT.maxlength
                && previewLength <= EMAIL_BROADCAST_PREVIEW.maxlength;
        },
    };

    if (section) {
        const validator = validators[section];

        if (validator) {
            return validator();
        }
    } else {
        let valid = true;

        Object.values(validators).forEach((validator) => {
            if (!validator()) {
                valid = false;
            }
        });

        return valid;
    }

    return false;
};

export const getEmptyUnlayerBroadcast = () => ({
    id: null,
    recipients: [],
    template: {
        design: {},
        from: DEFAULT_FROM_VALUE,
        toAddress: DEFAULT_TOADDRESS_VALUE,
        htmlBody: DEFAULT_TEMPLATE_HTML,
        id: '',
        name: '',
        title: '',
        previewText: '',
        subject: '',
        replyToAddress: '',
    },
    lastEdit: '',
    scheduleDate: null,
    timeZoneId: '',
    useContactTimeZone: true,
    broadcastState: BROADCAST_STATUS.DRAFT,
});

export const getEmptyMarketingSettingsInformation = () => ({
    companyCity: '',
    companyPhone: '',
    companyName: '',
    companyState: '',
    companyCountry: '',
    companyPostalCode: '',
    companyStreetAddress1: '',
    companyStreetAddress2: '',
});

export const getEmailLabel = ({ email, fullName }) => {
    return fullName ? `"${fullName}" <${email}>` : email;
};

export const getEmailName = (fromString) => {
    return (fromString.match(/^\s*(")?(.*)\1\s+/) ?? [])[2] || '';
};

export const getEmailValue = ({ email, fullName }, wrapSenderDisplayNameWithQuotesEnabled) => {
    if (wrapSenderDisplayNameWithQuotesEnabled) {
        return fullName ? `"${fullName}" <${email}>` : email;
    }

    return fullName ? `${fullName} <${email}>` : email;
};

export const getFromAddress = (from) => {
    const matches = from.match(/<(.*)>/) || [];

    return matches[1] || from;
};

export const mapToArray = (map) => {
    if (typeof map !== 'object' || map === null) { return new Error(''); }

    return Object.keys(map).map((key) => {
        return { value: key, label: map[key] };
    });
};


export const transformEmailTemplateToBroadcastTemplate = (template) => ({
    ...UNLAYER_BROADCAST_TEMPLATE_COMMAND,
    design: template?.builderJson?.content
        ? JSON.parse(template.builderJson.content)
        : {},
    html: template.html ?? '',
    id: template.id ?? '',
    title: template.title ?? ''
});


export const extractUnlayerBroadcastPayload = (email) => ({
    id: email.id,
    emailTemplate: {
        externalTemplateId: email.templateId,
        from: email[STEPS.SENDER].from,
        htmlBody: email[STEPS.CONTENT].htmlBody,
        previewText: email[STEPS.SUBJECT].previewText,
        subject: email[STEPS.SUBJECT].subject,
        title: email.templateTitle,
        toAddress: email[STEPS.AUDIENCE].toAddress,
        replyToAddress: email[STEPS.SENDER].replyToAddress,
    },
    scheduleDate: email[STEPS.SCHEDULE].scheduleDate,
    scheduleDateWithoutTimeZone: email[STEPS.SCHEDULE].scheduleDateWithoutTimeZone,
    timeZoneId: email[STEPS.SCHEDULE].timeZoneId,
    useContactTimeZone: email[STEPS.SCHEDULE].useContactTimeZone,
});


const doesTemplateHaveDesign = (template) => template?.builderJson?.content
    && typeof template?.builderJson?.content === 'string'
    && template?.builderJson?.content !== DEFAULT_BUILDER_JSON_CONTENT_STRING;


export const combineBroadcastAndTemplate = (broadcast, template) => {
    const design = doesTemplateHaveDesign(template)
        ? JSON.parse(template.builderJson.content)
        : {};

    const result = {
        source: SOURCES.BROADCAST,
        id: broadcast.id,
        broadcastState: broadcast.broadcastState,
        lastEdit: broadcast.lastEdit,

        marketingEmailId: broadcast.emailTemplate.id,
        templateId: broadcast.emailTemplate.externalTemplateId,
        templateTitle: broadcast.emailTemplate.title || template.templateTitle,

        [STEPS.SENDER]: {
            from: broadcast.emailTemplate.from,
            replyToAddress: broadcast.emailTemplate.replyToAddress,
        },
        [STEPS.SUBJECT]: {
            subject: broadcast.emailTemplate.subject,
            previewText: broadcast.emailTemplate.previewText,
        },
        [STEPS.AUDIENCE]: {
            toAddress: broadcast.emailTemplate.toAddress,
            recipients: broadcast.recipients,
            recipientStatistics: broadcast.emailBroadcastRecipientStatistics,
        },
        [STEPS.SCHEDULE]: {
            scheduleDate: broadcast.scheduleDate,
            scheduleDateWithoutTimeZone: broadcast.scheduleDateWithoutTimeZone,
            timeZoneId: broadcast.timeZoneId,
            useContactTimeZone: broadcast.useContactTimeZone,
        },
        [STEPS.CONTENT]: {
            design,
            htmlBody: broadcast.emailTemplate.htmlBody,
        },
    };

    return result;
};

export function convertMergeField(mergeField) {
    switch (mergeField) {
    case LEGACY_RECIPIENT_VALUES.EMAIL:
        return LIQUID_RECIPIENT_VALUES.EMAIL;

    case LEGACY_RECIPIENT_VALUES.EMAIL2:
        return LIQUID_RECIPIENT_VALUES.EMAIL2;

    case LEGACY_RECIPIENT_VALUES.EMAIL3:
        return LIQUID_RECIPIENT_VALUES.EMAIL3;

    default:
        return mergeField;
    }
}
